
import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Trip } from "@/types/itinerary";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/auth";

export const useItineraryTrip = (contextTrip: Trip | null) => {
  const [searchParams] = useSearchParams();
  const tripIdFromQuery = searchParams.get("trip");
  const { tripId: tripIdFromParams } = useParams<{ tripId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Determine which trip ID to use - prefer route params, then query params
  const tripId = tripIdFromParams || tripIdFromQuery;
  
  const [trip, setTrip] = useState<Trip | null>(contextTrip || null);
  const [isLoading, setIsLoading] = useState(!contextTrip);
  const [error, setError] = useState<string | null>(null);
  
  const fetchTripData = async () => {
    // If we already have trip data from context, skip this
    if (contextTrip) {
      setTrip(contextTrip);
      return;
    }
    
    // Check for authentication first
    if (!user) {
      console.log("User not authenticated, redirecting to auth page");
      navigate("/auth");
      toast.info("Please sign in to view trip details");
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      if (!tripId) {
        console.log("No trip ID provided, redirecting to trips page");
        navigate("/trips");
        toast.info("Please select a trip to view its itinerary");
        return;
      }
      
      console.log("Fetching trip by ID:", tripId);
      const tripQuery = supabase
        .from("trips")
        .select("*")
        .eq("id", tripId)
        .single();
      
      const { data, error } = await tripQuery;
        
      if (error) {
        console.error("Error fetching trip:", error);
        throw error;
      }
      
      if (data) {
        console.log("Trip data fetched:", data);
        setTrip(data);
      } else {
        throw new Error("Trip not found");
      }
    } catch (error) {
      console.error("Error in fetchTripData:", error);
      setError("Failed to load trip information. Please check the URL and try again.");
      toast.error("Failed to load trip information");
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    fetchTripData();
  }, [tripId, contextTrip, user]);
  
  return { trip, isLoading, error };
};
